import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Images,
    useModal,
    goalDataTypeEnums,
    sessionStatusEnums,
    trialStatusEnums,
    FindLoad,
    FindSuccessItem,
    maintenanceBadges,
} from 'utils';
import { httpRequestsOnLoadActions, httpRequestsOnSuccessActions } from 'store';
import {
    checkPrevNext,
    Duration,
    Frequency,
    Irt,
    Latency,
    Percent,
    Rate,
    Rating,
    Scale,
    trialCapMessage,
    useTrialCap,
    WholeInterval,
} from './constants';
import { authService } from '../../../../../store/clinical/session/session.service';
import { NotError, SimpleTooltip, StatusCard } from 'components';
import { RateHistoryLog, TrialHistoryLog, DurationHistoryLog, DELETE_TRIAL_LOG_ACTION_TYPE } from '../trialHistory';
import { TrialCircles } from './trialCircles';
import { renderTrialAverageRow } from '../../../../client/clientSingle/clientClinical/goals/trialData/constants';
import { toast } from 'react-toastify';

const ACTION_TYPE_SUCCESS = 'SAVE_SESSION_TARGET_OPTION_SUCCESS';
export const SessionTargetListCard = ({ item, goal, isLast, timerInfo, setTimerInfo }) => {
    const sessionById = useSelector((state) => state.session.sessionById);
    const dispatch = useDispatch();
    // Mirror sessionTargetGridCard: skip IN_PROGRESS rows so a still-running timer
    // doesn't surface as the "current" trial in navigation/labels.
    const completedTrials =
        item?.tSummary?.trials?.filter((t) => t?.status !== trialStatusEnums.IN_PROGRESS) ||
        item?.tSummary?.trials ||
        [];
    const [index, setIndex] = useState(completedTrials.length + 1 || 1);
    const current = completedTrials[index - 1];
    const { maxTrials, creationBlocked, completedAtCap } = useTrialCap(goal, item, completedTrials, index, setIndex);
    // At the cap there is no empty "next trial" slot to step onto.
    const nextBlockedByCap = completedAtCap && index >= completedTrials.length;
    // Set while no new trial may start — disables + explains every start control.
    const capMessage = creationBlocked ? trialCapMessage(maxTrials) : null;
    const checkPrevNextCase = checkPrevNext(goal);
    const { open } = useModal();
    const disableActions = sessionById?.status !== sessionStatusEnums.IN_PROGRESS;
    const loader = FindLoad(item?.id);
    const disablePrevNext = loader && loader?.find((i) => i?.params?.id === item?.id);
    const deleteTrialSuccess = FindSuccessItem(DELETE_TRIAL_LOG_ACTION_TYPE);

    useEffect(() => {
        if (deleteTrialSuccess && deleteTrialSuccess?.data === item?.tSummary?.id) {
            setIndex(item?.tSummary?.trials?.length + 1 || 1);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(DELETE_TRIAL_LOG_ACTION_TYPE));
        }
    }, [deleteTrialSuccess]);

    // Auto-advance `index` when a remote write grows completedTrials and we
    // were sitting on the empty slot — see sessionTargetGridCard for the full
    // rationale (Tab B stays stuck on Tab A's just-completed trial otherwise,
    // which blocks prev/next and hides the play UI).
    const prevCompletedLengthRef = useRef(completedTrials.length);
    useEffect(() => {
        const prev = prevCompletedLengthRef.current;
        prevCompletedLengthRef.current = completedTrials.length;
        if (completedTrials.length > prev && index === prev + 1) {
            setIndex(completedTrials.length + 1);
            dispatch(httpRequestsOnLoadActions.removeLoading(item?.id));

            if(timerInfo){
               setTimerInfo((prev) => [...prev?.filter((i) => i?.id !== item?.id)]);
            }
        }
    }, [completedTrials?.length]);

    // Returns a Promise resolving to the saved tSummary so timer components can
    // capture the IN_PROGRESS trial id from Play and reuse it on Stop. See
    // sessionTargetGridCard.saveTSummary for the matching contract — both cards
    // share the same set of timer components and must speak the same protocol.
    const saveTSummary = (option) => {
        if (sessionById?.status !== sessionStatusEnums.IN_PROGRESS) {
            return Promise.reject(new Error('Session not in progress'));
        }
        const status = option?.status;
        const isCompletion = !status || status === trialStatusEnums.COMPLETED;
        const explicitTrialId = option?.trialId !== undefined;
        // See sessionTargetGridCard for the rationale: skip loading/success
        // dispatches on debounced count saves so the UI stays calm.
        const isInProgressUpdate = status === trialStatusEnums.IN_PROGRESS && explicitTrialId && !!option.trialId;
        // See sessionTargetGridCard for the full rationale — `current?.id`
        // fallback is only correct for option-button data types. For timer
        // types, `current` can latch onto another clinician's COMPLETED trial
        // (stale `index`) and hijack our save into theirs.
        const TIMER_TYPES = [
            goalDataTypeEnums.DURATION,
            goalDataTypeEnums.RATE,
            goalDataTypeEnums.LATENCY,
            goalDataTypeEnums.IRT,
            goalDataTypeEnums.WHOLE_INTERVAL,
            goalDataTypeEnums.PARTIAL_INTERVAL,
            goalDataTypeEnums.MOMENTARY_TIME_SAMPLING,
        ];
        const resolvedTrialId = explicitTrialId
            ? option.trialId
            : TIMER_TYPES.includes(goal?.dataType)
                ? null
                : current?.id || null;
        if (!resolvedTrialId && creationBlocked) {
            // Safety net only — start controls are already disabled at the cap.
            // Resolve (not reject): Stop handlers fire-and-forget this promise.
            toast(<NotError type={'error'} errMessage={capMessage} />);
            return Promise.resolve(null);
        }
        const params = {
            goalId: goal?.id,
            targetId: item?.id,
            sessionId: sessionById?.id,
            trialId: resolvedTrialId,
            label: option?.label,
            value: option?.value,
        };
        if (status) {
            params.status = status;
        }
        if (option?.isPaused !== undefined) {
            params.isPaused = option.isPaused;
        }
        // Optimistic-concurrency: see sessionTargetGridCard for the rationale —
        // backend rejects a stale update with a silent no-op so two users
        // editing the same trial resolve as first-writer-wins. Skipped for
        // IN_PROGRESS updates so rapid same-user pause/resume/checkpoint
        // sequences don't silently drop when the second click fires before
        // the first response lands (stale render `item` → stale version).
        if (resolvedTrialId && !isInProgressUpdate) {
            const trialBeingUpdated = item?.tSummary?.trials?.find((t) => t?.id === resolvedTrialId);
            if (trialBeingUpdated && trialBeingUpdated.version !== undefined) {
                params.expectedVersion = trialBeingUpdated.version;
            }
        }

        if (
            goal?.dataType === goalDataTypeEnums.DURATION ||
            goal?.dataType === goalDataTypeEnums.RATE ||
            goal?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
            goal?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
            goal?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING ||
            goal?.dataType === goalDataTypeEnums.IRT ||
            goal?.dataType === goalDataTypeEnums.LATENCY
        ) {
            params['startTime'] = option?.startTime;
            params['endTime'] = option?.endTime;
        }

        if (!isInProgressUpdate) {
            dispatch(httpRequestsOnLoadActions.appendLoading(item?.id, params));
            dispatch(httpRequestsOnSuccessActions.removeSuccess(item?.id));
        }
        return authService
            .saveSessionTargetOptionService(params)
            .then((res) => {
                dispatch({ type: ACTION_TYPE_SUCCESS, payload: res?.data });
                if (isCompletion) {
                    setIndex(index + 1);
                }
                if (!isInProgressUpdate) {
                    dispatch(httpRequestsOnLoadActions.removeLoading(item?.id));
                }
                // Suppress success on IN_PROGRESS — see sessionTargetGridCard for the rationale.
                if (isCompletion) {
                    dispatch(httpRequestsOnSuccessActions.appendSuccess(item?.id));
                }
                return res?.data;
            })
            .catch((err) => {
                toast(<NotError type={'error'} errMessage={err?.data?.message || 'Something went wrong'} />);
                if (!isInProgressUpdate) {
                    dispatch(httpRequestsOnLoadActions.removeLoading(item?.id));
                    dispatch(httpRequestsOnSuccessActions.removeSuccess(item?.id));
                }
                throw err;
            });
    };

    const openHistory = () => {
        if (goal?.dataType === goalDataTypeEnums.RATE) {
            open(<RateHistoryLog goal={item} />);
        } else if (goal?.dataType === goalDataTypeEnums.DURATION || goal?.dataType === goalDataTypeEnums.IRT) {
            open(<DurationHistoryLog goal={item} />);
        } else {
            open(<TrialHistoryLog goal={item} />);
        }
    };

    return (
        <div className="target-list-card-wrapper" style={isLast ? { borderBottom: 'none', paddingBottom: 0 } : {}}>
            <div className="flex-align-center" style={{ gap: 24, borderRight: '1px solid #D0D5DD' }}>
                <div className="target-list-info-box">
                    <p>{item?.label || item?.name}</p>
                </div>

                <div className="target-list-card-btn-wrapper">
                    {checkPrevNextCase && (
                        <>
                            <div className="target-list-card-btn">
                                <button
                                    disabled={index === 1 || disablePrevNext}
                                    className="list-prev-btn"
                                    onClick={() => setIndex(index - 1)}
                                    style={{
                                        transform: 'rotate(90deg)',
                                        opacity: index === 1 || disablePrevNext ? 0.5 : 1,
                                    }}
                                >
                                    <img src={Images.chevronDown} alt="arrow" />
                                </button>
                                <p>{`${index}/${goal?.minTrials}`}</p>
                                <SimpleTooltip title={nextBlockedByCap ? trialCapMessage(maxTrials) : ''} placement="top" arrow>
                                <span style={{ display: 'inline-flex' }}>
                                <button
                                    disabled={!current?.id || disablePrevNext || nextBlockedByCap}
                                    onClick={() => setIndex(index + 1)}
                                    className="list-next-btn"
                                    style={{
                                        opacity: !current?.id || disablePrevNext || nextBlockedByCap ? 0.5 : 1,
                                    }}
                                >
                                    <img src={Images.chevronDown} alt="arrow" />
                                </button>
                                </span>
                                </SimpleTooltip>
                            </div>

                            <TrialCircles current={current} item={item} />
                        </>
                    )}
                </div>
            </div>

            <div className="flex-align-center full-width" style={{ gap: 24 }}>
                {goal?.dataType === goalDataTypeEnums.PERCENT && (
                    <Percent
                        index={index}
                        item={item}
                        loader={loader}
                        goal={goal}
                        current={current}
                        saveTSummary={saveTSummary}
                        grid={true}
                        disableActions={disableActions} capMessage={capMessage}
                    />
                )}

                {goal?.dataType === goalDataTypeEnums.RATING && (
                    <Rating
                        index={index}
                        item={item}
                        loader={loader}
                        goal={goal}
                        current={current}
                        saveTSummary={saveTSummary}
                        grid={true}
                        disableActions={disableActions} capMessage={capMessage}
                    />
                )}

                {goal?.dataType === goalDataTypeEnums.LATENCY && (
                    <Latency
                        index={index}
                        item={item}
                        loader={loader}
                        saveTSummary={saveTSummary}
                        current={current}
                        goal={goal}
                        disableActions={disableActions} capMessage={capMessage}
                        width={'40px'}
                    />
                )}

                {goal?.dataType === goalDataTypeEnums.DURATION && (
                    <Duration index={index} item={item} loader={loader} saveTSummary={saveTSummary} disableActions={disableActions} capMessage={capMessage} />
                )}

                {goal?.dataType === goalDataTypeEnums.FREQUENCY && (
                    <Frequency
                        index={index}
                        item={item}
                        loader={loader}
                        saveTSummary={saveTSummary}
                        current={current}
                        disableActions={disableActions} capMessage={capMessage}
                    />
                )}

                {goal?.dataType === goalDataTypeEnums.SCORE && (
                    <Scale
                        index={index}
                        item={item}
                        loader={loader}
                        saveTSummary={saveTSummary}
                        current={current}
                        grid={true}
                        disableActions={disableActions} capMessage={capMessage}
                    />
                )}

                {(goal?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
                    goal?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
                    goal?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING) && (
                    <WholeInterval
                        index={index}
                        item={item}
                        loader={loader}
                        goal={goal}
                        saveTSummary={(it) => {
                            const result = saveTSummary(it);
                            if (!it?.status || it.status === trialStatusEnums.COMPLETED) {
                                timerInfo && setTimerInfo((prev) => [...prev?.filter((i) => i?.id !== item?.id)]);
                            }
                            return result;
                        }}
                        current={current}
                        disableActions={disableActions} capMessage={capMessage}
                        setTimerInfo={(info) => {
                            setTimerInfo((prev) => {
                                const filtered = prev?.filter((i) => i?.id !== item?.id) || [];
                                if (info?.active) {
                                    return [...filtered, info];
                                }
                                return filtered;
                            });
                        }}
                    />
                )}

                {goal?.dataType === goalDataTypeEnums.RATE && (
                    <Rate index={index} item={item} loader={loader} saveTSummary={saveTSummary} disableActions={disableActions} capMessage={capMessage} />
                )}

                {goal?.dataType === goalDataTypeEnums.IRT && (
                    <Irt goal={goal} index={index} item={item} loader={loader} saveTSummary={saveTSummary} disableActions={disableActions} capMessage={capMessage} current={current}/>
                )}
            </div>

            <div className="flex-align-center" style={{ gap: 12 }}>
                {item?.isMaintenance && (
                    <div className="meintenance-list-box">
                        <StatusCard
                            status={maintenanceBadges.RUNNING.label}
                            custom={maintenanceBadges.RUNNING}
                        />
                    </div>
                )}

                <div className="session-target-info-box">
                    {!!item?.tSummary?.trials?.length && (
                        <div className="goal-progress-box" style={{ whiteSpace: 'nowrap' }}>
                            {renderTrialAverageRow(
                                {
                                    ...item,
                                    ...item?.tSummary?.trialData,
                                    dataType: item?.tSummary?.dataType,
                                },
                                'reset'
                            )}
                        </div>
                    )}

                    {item?.masteryInfo && !item?.isMaintenance && <p className="session-target-mastery-info">{item?.masteryInfo}</p>}
                </div>

                {!!item?.tSummary?.trials?.length && (
                    <button style={{ height: 36, width: 36 }} className="session-gray-action-btn-box" onClick={openHistory}>
                        <img src={Images.historyTab} alt="resetTimmer" />
                    </button>
                )}
            </div>
        </div>
    );
};
